import { useState } from 'react';

interface Props {
  wordsUsed: number;
  freeLimit: number;
  onContinue: (email: string | null) => void;
  onClose: () => void;
}

export default function EmailCaptureModal({ wordsUsed, freeLimit, onContinue, onClose }: Props) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const usedPct = Math.min((wordsUsed / freeLimit) * 100, 100);

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('email', email);
      body.append('words_used', String(wordsUsed));
      await fetch('https://readdy.ai/api/form/d72gs52aegmdqoca7u60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch { /* ignore */ }
    setSubmitted(true);
    setTimeout(() => onContinue(email), 1200);
  };

  return (
    <div
      className="fixed inset-0 z-[10002] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl bg-white"
        onClick={e => e.stopPropagation()}
        data-readdy-form
      >
        {/* Top gradient band */}
        <div className="relative h-36 bg-gradient-to-br from-violet-600 via-violet-500 to-cyan-500 flex flex-col items-center justify-center px-6">
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px), radial-gradient(circle at 70% 30%, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
          />
          {/* Usage ring */}
          <div className="relative w-16 h-16 mb-1">
            <svg className="-rotate-90 w-16 h-16">
              <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="5" />
              <circle
                cx="32" cy="32" r="26" fill="none" stroke="white" strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 26}`}
                strokeDashoffset={`${2 * Math.PI * 26 * (1 - usedPct / 100)}`}
                style={{ transition: 'stroke-dashoffset 1s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <i className="ri-lock-2-fill text-white text-xl"></i>
            </div>
          </div>
          <p className="text-white text-sm font-extrabold text-center leading-tight">Free limit reached</p>
        </div>

        {/* Body */}
        <div className="px-7 pt-6 pb-2">
          <h2 className="text-xl font-extrabold text-gray-900 text-center leading-tight mb-1">
            Want us to hold your spot?
          </h2>
          <p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">
            Drop your email and we&apos;ll send you a reminder + exclusive discount before you upgrade.
          </p>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-violet-50 rounded-2xl p-3 text-center">
              <p className="text-lg font-extrabold text-violet-700">{wordsUsed.toLocaleString()}</p>
              <p className="text-xs text-violet-500 font-semibold mt-0.5">Words used</p>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-3 text-center">
              <p className="text-lg font-extrabold text-emerald-700">∞</p>
              <p className="text-xs text-emerald-500 font-semibold mt-0.5">Pro words</p>
            </div>
            <div className="bg-amber-50 rounded-2xl p-3 text-center">
              <p className="text-lg font-extrabold text-amber-700">50%</p>
              <p className="text-xs text-amber-500 font-semibold mt-0.5">Upgrade off</p>
            </div>
          </div>

          {/* Form */}
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder="your@email.com"
                  required
                  className="w-full pl-10 pr-4 py-3.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                />
              </div>
              {error && (
                <p className="text-xs text-rose-500 font-medium flex items-center gap-1.5 px-1">
                  <i className="ri-error-warning-line"></i>{error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-bold hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:scale-100"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-base"></i>Saving…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-mail-send-line text-base"></i>Save my spot &amp; see plans
                  </span>
                )}
              </button>
            </form>
          ) : (
            <div className="flex flex-col items-center justify-center py-4 gap-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-100">
                <i className="ri-check-line text-emerald-600 text-2xl"></i>
              </div>
              <p className="text-sm font-bold text-gray-800 text-center">
                Got it! Showing you the plans now…
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-4 flex items-center justify-between">
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <i className="ri-shield-check-line text-emerald-500"></i>
            No spam. Unsubscribe anytime.
          </p>
          <button
            onClick={() => onContinue(null)}
            className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Skip, show me plans
          </button>
        </div>
      </div>
    </div>
  );
}
